import { ReadonlyVec3 } from 'gl-matrix';
import { atom, atomFamily, DefaultValue, selector } from 'recoil';
import type { RoiId } from '~/domain/changes/ids';
import type { ContourTexture } from '~/engine/data/structure/ContourTexture';
import {
    VoiShape,
    VoxelSubRange,
    WorldBoundsMm,
} from '~/domain/services/imageService/gridGeometry';
import { BrushMode } from '~/engine/tool/actions/contouring-actions/BrushStamp';
import { RegistrationMode } from '~/engine/tool/tools/RegistrationTool';
import { PostProcessingMode } from '~/engine/util/algorithms/sdf/SDFPostProcessing';

export type Tools =
    | 'pan'
    | 'boxZoom'
    | 'rotateView'
    | 'brush'
    | 'line'
    | 'registration'
    | 'transform'
    | 'lineDeform'
    | 'deform'
    | 'smoothBrush'
    | 'poi-move'
    | 'cross-section'
    | 'measurement'
    | 'intensity'
    | 'rectangle-select';

/** Tools whose strokes edit the selected structure's contours on the displayed slices. */
export const CONTOUR_STROKE_TOOLS: readonly Tools[] = [
    'brush',
    'line',
    'deform',
    'lineDeform',
    'smoothBrush',
    'rectangle-select',
];

/**
 * What a tool changes when the user drags with it: the view or its
 * annotations, the contours of the selected structure, the position of a
 * point ROI, or the registration of a fused pair.
 */
type ToolEffect = 'view' | 'contour' | 'point' | 'registration';

/**
 * The effect of every tool. Keyed on {@link Tools}, so a new tool without an
 * effect does not compile.
 */
const TOOL_EFFECT: Record<Tools, ToolEffect> = {
    pan: 'view',
    boxZoom: 'view',
    rotateView: 'view',
    measurement: 'view',
    intensity: 'view',
    brush: 'contour',
    line: 'contour',
    deform: 'contour',
    lineDeform: 'contour',
    smoothBrush: 'contour',
    'rectangle-select': 'contour',
    transform: 'contour',
    'cross-section': 'contour',
    'poi-move': 'point',
    registration: 'registration',
};

/**
 * Whether `tool` changes only the view or its annotations, and writes to no
 * structure and no registration.
 */
export const onlyChangesView = (tool: Tools): boolean =>
    TOOL_EFFECT[tool] === 'view';

/** Whether `tool` writes to the contours of the selected structure. */
export const writesContours = (tool: Tools): boolean =>
    TOOL_EFFECT[tool] === 'contour';

/**
 * Whether `tool` writes to the selected structure: its contours or the
 * position of its point.
 */
export const writesStructure = (tool: Tools): boolean =>
    writesContours(tool) || TOOL_EFFECT[tool] === 'point';

/** Whether `tool` moves the selected structure, or point, as a whole. */
export const isMoveTool = (tool: Tools): boolean =>
    tool === 'transform' || tool === 'poi-move';

export type Operations =
    | 'interpolation'
    | 'margin'
    | 'smoothing'
    | 'grid-layout-resizing'
    | 'boolean-operation';

export const minSliceInterval = 2; // Minimum value for keeping every nth slice

/**
 * World-space (patient mm) bounds of the shared VOI selection box. An
 * alias of the domain {@link WorldBoundsMm} shape so the overlay, the
 * registration region and the body-contour restriction all speak one type.
 */
export type VoiBounds = WorldBoundsMm;

export const selectedToolState = atom<Tools>({
    key: 'selectedToolState',
    default: 'pan',
});

export const zoomToCursorState = atom<boolean>({
    key: 'zoomToCursorState',
    default: true,
});

export const selectedOperationState = atom<Operations | null>({
    key: 'selectedOperationState',
    default: null,
});

/**
 * Dimensionality of a contouring stroke. In 3D mode the stroke acts on
 * a volume around the cursor; in 2D mode it is confined to the displayed
 * slice of the view being stroked.
 */
export type StrokeMode = '2d' | '3d';

/**
 * Options of the brush tools and of the eraser's brush shape. The
 * eraser is the brush with {@link erase} on, so the diameter and the
 * stroke mode carry over between drawing and erasing.
 */
export type BrushOptions = {
    diameter: number;
    mode: BrushMode;
    erase: boolean;
    /** In 3D mode the brush is a sphere of the brush diameter. */
    strokeMode: StrokeMode;
};

export const brushOptionsState = atom<BrushOptions>({
    key: 'brushOptionsState',
    default: {
        diameter: 20,
        mode: 'static',
        erase: false,
        // 2D is the safer default: the effect is confined to the slice
        // under the cursor, which is what the viewport shows.
        strokeMode: '2d',
    },
});

export const contourClearanceModeState = atom<'current' | 'all'>({
    key: 'contourClearanceModeState',
    default: 'current',
});

/**
 * Bounding-ROI drawing constraint. When {@link enabled}, contouring tools
 * (brush, adaptive brush, line) clip their additive drawing to the interior
 * of the structure identified by {@link roiId}, so a contour can never
 * extend beyond the selected bounding structure.
 */
export type BoundingRoiConstraint = {
    enabled: boolean;
    /** The bounding structure, or null when none selected. */
    roiId: RoiId | null;
};

export const boundingRoiConstraintState = atom<BoundingRoiConstraint>({
    key: 'boundingRoiConstraintState',
    default: { enabled: false, roiId: null },
});

/**
 * Collision drawing constraint. When {@link enabled}, contouring tools
 * subtract the union of the {@link roiIds protected structures} from
 * their additive stroke, so a stroke can never overwrite a protected
 * neighbour (e.g. drawing a GTV up against spinal cord / brainstem).
 */
export type CollisionConstraint = {
    enabled: boolean;
    /** The protected structures (the checked set). */
    roiIds: RoiId[];
};

export const collisionConstraintState = atom<CollisionConstraint>({
    key: 'collisionConstraintState',
    default: { enabled: false, roiIds: [] },
});

/**
 * Intensity-threshold drawing constraint. When {@link enabled}, additive
 * contouring is deposited only on voxels whose underlying image intensity falls
 * within `[lower, upper]`. The bounds are stored in the image's **display units**
 * — Hounsfield Units (HU) for CT, SUVbw for PET — because the UI (the slider /
 * presets) is the source of truth; conversion to the image's raw voxel scale
 * happens at the engine boundary (see `displayRangeToRaw` in
 * `~/engine/tool/actions/contouring-actions/intensityThreshold`). Like the
 * other modifiers it only
 * affects draw (additive) strokes; erasing is never constrained.
 */
export type IntensityThresholdConstraint = {
    enabled: boolean;
    /** Lower bound in display units (HU for CT, SUVbw for PET). */
    lower: number;
    /** Upper bound in display units (HU for CT, SUVbw for PET). */
    upper: number;
    /**
     * Whether `lower`/`upper` hold a real, user-visible band (from the one-time
     * default seed or a manual change) rather than the unseeded `{0, 0}`
     * placeholder. Persisted here (not in a component ref) so re-enabling,
     * switching image, or the control remounting never re-seeds over a
     * deliberate band; the engine only applies the threshold once `seeded`.
     */
    seeded: boolean;
};

export const intensityThresholdConstraintState =
    atom<IntensityThresholdConstraint>({
        key: 'intensityThresholdConstraintState',
        default: { enabled: false, lower: 0, upper: 0, seeded: false },
    });

export type LineOptions = {
    width: number;
    color: string;
    mode: 'draw' | 'erase' | null;
};

export const lineOptionsState = atom<LineOptions>({
    key: 'lineOptionsState',
    default: {
        width: 2,
        color: '#050505',
        mode: null,
    },
});

/**
 * The extrusion depth a rectangle delete starts in.
 *
 * 2D, because the gesture is drawn on ONE slice and what it is almost
 * always removing is a stray region on the slice in front of the user. In
 * 3D the rectangle cuts through every slice of the structure, including
 * slices the user never looked at — the deletion they cannot see is the
 * one they cannot check. 3D stays one click away for the case where
 * clearing the whole column IS the intent.
 */
const DEFAULT_RECTANGLE_SELECT_MODE: PostProcessingMode = '2D';

/**
 * Extrusion depth for the rectangle-select tool, chosen by a
 * {@link ModeToggle} while the tool is armed: 2D extrudes the drawn
 * rectangle across the slices the drag spans, 3D through the whole volume.
 * It selects nothing about connectivity — the delete is a voxel-window
 * clear. A session-scoped atom (like {@link brushOptionsState}) rather than
 * component state, so the tool is armed with it and no component passes a
 * callback into the engine.
 */
export const rectangleSelectModeState = atom<PostProcessingMode>({
    key: 'rectangleSelectModeState',
    default: DEFAULT_RECTANGLE_SELECT_MODE,
});

/**
 * The most recent rectangle a user dragged with the rectangle-select tool,
 * written by the `onSelect` callback of the armed {@link RectangleSelectTool}.
 *
 * `null` is the "nothing pending" resting state, and the consumer resets
 * the atom to it as it takes each drag. That reset — not a request id — is
 * what separates one drag from the next: two rectangles CAN be
 * bitwise-identical (a user re-dragging the same spot), and a set() back
 * to a non-null value after a null always re-fires the consuming effect
 * regardless of whether the box compares equal.
 *
 * No drag survives the consumer's teardown: it clears the atom on unmount
 * as well, so a rectangle that lands in the same commit as the teardown is
 * dropped rather than replayed on the next mount against whatever ROI is
 * selected by then.
 */
export const rectangleSelectionState = atom<{
    range: VoxelSubRange;
} | null>({
    key: 'rectangleSelectionState',
    default: null,
});

export type LineDeformOptions = {
    spreadPercent: number; // percentage of contour length, range [1, 100]
};

export const lineDeformOptionsState = atom<LineDeformOptions>({
    key: 'lineDeformOptionsState',
    default: {
        spreadPercent: 30,
    },
});

/** Whether Undo / Redo can step through cross-sections drawn but not applied, and whether any are drawn. */
export const crossSectionDrawingHistoryState = atom<{
    canUndo: boolean;
    canRedo: boolean;
    hasDrawing: boolean;
}>({
    key: 'crossSectionDrawingHistoryState',
    default: { canUndo: false, canRedo: false, hasDrawing: false },
});

/** Monotonic counter bumped when the drawn contours change, requesting a preview recompute. */
export const crossSectionShapeRequestState = atom<number>({
    key: 'crossSectionShapeRequestState',
    default: 0,
});

export const crossSectionPreviewReadyState = atom<boolean>({
    key: 'crossSectionPreviewReadyState',
    default: false,
});

/** Monotonic counter bumped by the tray's Apply button. */
export const crossSectionApplyRequestState = atom<number>({
    key: 'crossSectionApplyRequestState',
    default: 0,
});

/**
 * The uncommitted Cross-Section shape on screen, as a handle: `texture` is
 * deleted when the next preview replaces it, so observers copy out what they
 * need when `revision` moves, and read it only while `GlOperationGuard` is idle.
 */
export interface CrossSectionPreviewHandle {
    readonly texture: ContourTexture;
    readonly color: ReadonlyVec3;
    readonly revision: number;
}

// Recoil must not freeze the texture: it holds live GPU handles.
export const crossSectionPreviewHandleState =
    atom<CrossSectionPreviewHandle | null>({
        key: 'crossSectionPreviewHandleState',
        default: null,
        dangerouslyAllowMutability: true,
    });

/**
 * Dimensionality of the deform brush. In 3D mode the stroke
 * warps the SDF volumetrically around the cursor; in 2D mode
 * the warp is confined to the current slice of the view
 * being dragged.
 */
export type DeformMode = '2d' | '3d';

export type DeformOptions = {
    diameter: number; // mm, range [2, 50]
    mode: StrokeMode;
};

export const deformOptionsState = atom<DeformOptions>({
    key: 'deformOptionsState',
    default: {
        diameter: 20,
        mode: '3d',
    },
});

/**
 * Options of the local smoothing brush. Strength (1-5) selects the
 * Gaussian sigma applied per pass (mm, see SmoothBrushRenderer);
 * mode reuses the deform brush's 2D/3D semantics.
 */
export type SmoothBrushOptions = {
    diameter: number; // mm, range [2, 50]
    strength: number; // 1-5
    mode: StrokeMode;
};

export const smoothBrushOptionsState = atom<SmoothBrushOptions>({
    key: 'smoothBrushOptionsState',
    default: {
        diameter: 20,
        strength: 3,
        // 2D is the safer default: the effect is confined to the slice
        // under the cursor, which is what the viewport shows.
        mode: '2d',
    },
});

/**
 * When true, the slice-to-slice contour deformation suggestion
 * feature is active. While enabled, after the user draws on a
 * slice and navigates to an adjacent empty slice, the system
 * proposes a preview contour derived from nearby contoured slices.
 * Suggestions are held client-side until accepted or discarded.
 */
export const contourAutoSuggestionEnabledState = atom<boolean>({
    key: 'sliceDeformEnabledState',
    default: false,
});

export const keepEveryNthSliceState = atom<boolean>({
    key: 'keepEveryNthSliceState',
    default: false,
});

export const keepEveryNthSliceValueState = atom<number>({
    key: 'keepEveryNthSliceValueState',
    default: minSliceInterval,
});

export type RegistrationToolOptions = {
    mode: RegistrationMode | null;
};

export const registrationModeState = atom<RegistrationToolOptions>({
    key: 'registrationModeState',
    default: { mode: null },
});

export const voiCheckboxState = atom<boolean>({
    key: 'voiCheckboxState',
    default: false,
});

export const autoRegistrationInProgressState = atom<boolean>({
    key: 'autoRegistrationInProgressState',
    default: false,
});

/** A just-created registration waiting to be aligned before its pair is shown. */
export interface PendingAutoRegistration {
    registrationSopInstanceUid: string;
    primarySeriesUid: string;
    secondarySeriesUid: string;
}

/**
 * The new registration aligned automatically before its pair is fused; null
 * when none is waiting.
 */
export const pendingAutoRegistrationState =
    atom<PendingAutoRegistration | null>({
        key: 'pendingAutoRegistrationState',
        default: null,
    });

export const voiBoundsState = atom<VoiBounds | null>({
    key: 'voiBoundsState',
    default: null,
});

/**
 * Body the shared VOI overlay's bounds select, for the consumer that
 * offers the choice. Only the drawing and the consumer's own compute
 * read it — the bounds, the handles and every other overlay behaviour
 * are identical for both shapes.
 *
 * Defaults to `'box'` and is reset there by the consumer that set it, so
 * a consumer with no shape control (registration, threshold
 * segmentation) can never inherit an ellipse from a panel that has
 * closed.
 */
export const voiShapeState = atom<VoiShape>({
    key: 'voiShapeState',
    default: 'box',
});

/**
 * Consumers of the shared VOI-selection overlay (the draggable box drawn
 * on every viewport). The overlay itself is operation-neutral: it
 * renders whenever a consumer is active and edits {@link voiBoundsState};
 * default bounds and parameter plumbing belong to each consumer.
 */
export type VoiConsumer =
    | 'registration'
    | 'threshold-segmentation'
    | 'post-processing-crop';

/**
 * Activation requested by non-registration consumers (today: the
 * threshold-segmentation panel's region). Registration is
 * deliberately NOT requested through this atom — its activation stays
 * derived from its existing tool + checkbox atoms so its behavior is
 * exactly as before the overlay was shared. The merged view every
 * reader should use is {@link activeVoiConsumerState}.
 */
export const voiConsumerRequestState = atom<Exclude<
    VoiConsumer,
    'registration'
> | null>({
    key: 'voiConsumerRequestState',
    default: null,
});

/**
 * Per-consumer "Restrict to region" state, held OUTSIDE the panel
 * components so it survives closing and reopening a panel (and the
 * toolbar-driven panel-type switch, which unmounts the content without
 * an onClose). A panel component owns only the viewport-editing session;
 * the user's intent to restrict, and the box they drew, are session
 * state.
 *
 * Keyed by consumer because two consumers' boxes are unrelated: the crop
 * tool's box frames what to keep, the threshold tool's box bounds an
 * extraction, and adopting one for the other would silently apply a
 * region the user drew for something else.
 *
 * `imageUid` stamps the box with the primary image it was drawn on. A
 * record whose uid does not match the current image reads as OFF with no
 * box — a region drawn on another scan is meaningless, and must never
 * silently clamp an operation on a scan the user never positioned it
 * on. Switching back to the original scan restores its own box, which is
 * the same guarantee stated the other way round.
 *
 * The BOX ITSELF lives in {@link voiBoundsState} only while the panel is
 * mounted and the restriction is on: leaving it there afterwards would
 * hand one consumer's box to registration, whose own seeding keeps any
 * bounds it finds already set.
 */
export interface VoiRegionRestriction {
    /** dicomUid of the primary image the box belongs to. */
    imageUid: string | null;
    enabled: boolean;
    boundsMm: VoiBounds | null;
}

const EMPTY_RESTRICTION: VoiRegionRestriction = {
    imageUid: null,
    enabled: false,
    boundsMm: null,
};

export const voiRegionRestrictionState = atomFamily<
    VoiRegionRestriction,
    Exclude<VoiConsumer, 'registration'>
>({
    key: 'voiRegionRestrictionState',
    default: EMPTY_RESTRICTION,
});

/**
 * The consumer the shared VOI overlay is currently active for, or null
 * when it is hidden. Registration wins over a simultaneous request: the
 * two can only overlap transiently (the registration tool can stay
 * selected while an operation panel opens), and while they do the drag
 * handles edit the one shared {@link voiBoundsState} either way.
 */
export const activeVoiConsumerState = selector<VoiConsumer | null>({
    key: 'activeVoiConsumerState',
    get: ({ get }) => {
        if (
            get(selectedToolState) === 'registration' &&
            get(voiCheckboxState)
        ) {
            return 'registration';
        }
        return get(voiConsumerRequestState);
    },
});

/**
 * True while a panel has parked the ROI-editing tools because its VOI box
 * is on screen (see `useParkedContouringTool`). Lives here, not in the
 * panel, because the toolbar that draws those tools is a sibling of every
 * panel that can raise a box: a parked tool must also READ as
 * unavailable, and its button is nowhere near the hook that parked it.
 */
export const contouringToolsParkedState = atom<boolean>({
    key: 'contouringToolsParkedState',
    default: false,
});

/** Whether the image tiles draw their scale bar. */
export const scaleDisplayEnabledState = atom<boolean>({
    key: 'scaleDisplayEnabledState',
    default: false,
});

export const histogramHintEnabledState = atom<boolean>({
    key: 'histogramHintEnabledState',
    default: false,
});

/**
 * Which prior version of the selected ROI the difference overlay highlights
 * against (green = added, red = removed), or 'off'. 'previous-slice' is the
 * cross-slice shader; 'pre-operation' and 'original-ai' are the same-slice
 * reference-diff overlay (see useReferenceDiff).
 */
export type DifferenceReferenceMode =
    | 'off'
    | 'previous-slice'
    | 'pre-operation'
    | 'original-ai';

export const differenceReferenceModeState = atom<DifferenceReferenceMode>({
    key: 'differenceReferenceModeState',
    default: 'off',
});

/**
 * How the tool options dock shows, shared by every tool and operation it
 * holds so it keeps its place across them. `mode` is `folded` while a
 * tool's options are folded to a tab, and `thin` while the dock is
 * collapsed to one line as the user works on a view. `detachedPosition` is
 * where the user dragged the dock, in CSS pixels from the top left of the
 * window, or null while it sits by the rail.
 */
export interface ToolOptionsDockUiState {
    mode: 'open' | 'thin' | 'folded';
    detachedPosition: { x: number; y: number } | null;
}

export const toolOptionsDockState = atom<ToolOptionsDockUiState>({
    key: 'toolOptionsDockState',
    default: { mode: 'open', detachedPosition: null },
});

/**
 * How many operation docks show. While any does, a tool's dock gives way
 * to it.
 */
export const shownOperationDocksState = atom<number>({
    key: 'shownOperationDocksState',
    default: 0,
});

/** The Post Processing panel's five icon tabs. */
export type PostProcessingTab = 'thin' | 'fill' | 'smooth' | 'parts' | 'crop';

/**
 * Single source of truth for the Post Processing tab strip's left-to-right
 * order. Both the rendered tab order and the list the toolbar button expands
 * into read from this array, so the two can never drift apart.
 */
export const POST_PROCESSING_TAB_ORDER: readonly PostProcessingTab[] = [
    'thin',
    'fill',
    'smooth',
    'parts',
    'crop',
];

export type OperationPanelState =
    | { type: 'closed' }
    | { type: 'boolean-operation' }
    | { type: 'margin'; mode: 'symmetric' | 'asymmetric' }
    | { type: 'ring' }
    | { type: 'threshold-segmentation' }
    | { type: 'couch' }
    | { type: 'interpolation' }
    // The active tab is panel state, not component state: the panel reopens
    // on the tab the user left, the same way it reopens where they moved it.
    | { type: 'post-processing'; tab: PostProcessingTab };

export const operationPanelState = atom<OperationPanelState>({
    key: 'operationPanelState',
    default: { type: 'closed' },
});

/**
 * True while the open operation previews or writes. The user can then
 * neither close the operation nor replace it with another one, because
 * either unmounts the operation in the middle of its work.
 */
export const operationBusyState = atom<boolean>({
    key: 'operationBusyState',
    default: false,
});

/**
 * A tool that does not only change the view, picked while the open
 * operation previews or writes. It waits here, off the views, and arms as
 * the operation closes. Null while no tool waits.
 */
export const heldToolState = atom<Tools | null>({
    key: 'heldToolState',
    default: null,
});

/**
 * The tool the user picks; reads as the tool on the views. While the open
 * operation previews or writes, a pick of a tool that does not only change
 * the view goes to {@link heldToolState} and leaves the views as they are.
 * Any other pick lets go of a held tool and arms at once.
 */
export const pickedToolState = selector<Tools>({
    key: 'pickedToolState',
    get: ({ get }) => get(selectedToolState),
    set: ({ get, set, reset }, tool) => {
        if (tool instanceof DefaultValue) {
            reset(heldToolState);
            reset(selectedToolState);
            return;
        }
        if (get(operationBusyState) && !onlyChangesView(tool)) {
            set(heldToolState, tool);
            return;
        }
        set(heldToolState, null);
        set(selectedToolState, tool);
    },
});

/**
 * The SUV Threshold panel's window maximum, published as ONE value so
 * the marker's position and its label can never disagree: the position
 * is the patient-space (mm) center of the voxel holding the maximum,
 * and suvValue is the SUVbw (g/ml) at that voxel — the SAME number the
 * panel's stats row shows, never a re-computation.
 */
export interface PetWindowMax {
    /** Patient-space (mm) center of the voxel holding the maximum. */
    positionMm: ReadonlyVec3;
    /** SUVbw (g/ml) at that voxel — the panel stats row's value. */
    suvValue: number;
}

/**
 * The SUV Threshold panel's window maximum — the SUVmax the panel
 * quotes, with the voxel it sits in — or null while the panel is closed
 * or its box holds no uptake. The panel publishes it (live: it rides
 * the same recompute as the SUVmax stat, so a box drag moves it) and
 * MaxIntensityMarkerOverlay draws the position with the value as its
 * text label in every plane view, so the user can verify the maximum
 * arises from the intended lesion and not from a hotter structure
 * clipped by the box edge.
 *
 * Deliberately NOT a {@link VoiConsumer} variant: the HU and SUV tabs
 * both report 'threshold-segmentation' to {@link activeVoiConsumerState},
 * so consumer-gating could not tell them apart and would arm the marker
 * for the HU presets too.
 */
export const petWindowMaxState = atom<PetWindowMax | null>({
    key: 'petWindowMaxState',
    default: null,
});

/** When true the user can drag POIs in the viewport overlay. */
export const poiMoveActiveState = atom<boolean>({
    key: 'poiMoveActiveState',
    default: false,
});

/** When true, transforming a SUPPORT structure moves all SUPPORT structures together. */
export const transformAllSupportState = atom<boolean>({
    key: 'transformAllSupportState',
    default: true,
});
